import { MutableTypedObject } from "@multiweb/types/react";
import { CancellationToken, CancellationTokenSource } from "Global/Base/Promises/CancellationTokenSource";
import { IDataSourceLoadInfo, IOrderBy } from "../../Global/Base/DataSources/Interfaces";
import { Event as GlobalEvent } from "../../Global/Base/Events";
import { PromiseUtils } from "../../Global/Base/Promises/PromiseUtils";
import { StringUtils } from "../../Global/Base/Utils/StringUtils";
import { registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { BaseTreeViewGrid } from "./BaseTreeViewGrid";
import { Column } from "./DataGridSubcontrols/Columns/Column";
import { CommentaryFrameDataRow } from "./DataGridSubcontrols/Components/CommentaryFrameDataRow";
import { EmptyTreeDataGridMessage } from "./DataGridSubcontrols/DataGridMessages/EmptyTreeDataGridMessage";
import { EmptyDatagridMessageType, IEmptyDataGridMessage } from "./DataGridSubcontrols/DataGridMessages/IEmptyDataGridMessage";
import { CommentaryFrameDataSource, ICommentaryDataSourceLoadInfo, ICommentRowData } from "./DataSource/CommentaryFrameDataSource";
import { IEmptyMessageDataGrid } from "./IDataGrid";
import { IDataGridDataShownEventData } from "./IEvent";

export class CommentaryFrameGrid extends BaseTreeViewGrid<CommentaryFrameDataRow> implements IEmptyMessageDataGrid<CommentaryFrameDataRow> {
    public replyButtonClicked: GlobalEvent<CommentaryFrameDataRow, void> = new GlobalEvent<CommentaryFrameDataRow, void>();

    protected _scrollListener: EventListener;
    protected _resizeListener: EventListener;
    //#region Fields
    private _dataSource: CommentaryFrameDataSource;
    private _dsRows: ICommentaryDataSourceLoadInfo;
    private _bindingData: boolean;
    /** Počítadlo aktuálně běžících loaderů pro vykreslení dat */
    private _dataLoadCounter: number = 0;
    private _currentSkippedRows: number = 0;
    private _authorColor: MutableTypedObject<string> = {};
    protected _allRowsMap: Map<string, CommentaryFrameDataRow> = new Map;
    //#endregion Fields

    //#region Properties
    public emptyMessage: IEmptyDataGridMessage;
    public override orderBy: IOrderBy[];
    public override columns: Column[];
    public override get dataSource(): CommentaryFrameDataSource {
        return this._dataSource;
    }

    public override set dataSource(v: CommentaryFrameDataSource) {
        this._dataSource = v;
        this._dataSource.dataLoaded.add((_sender, eventData) => {
            this._dataLoadCounter--;
            this._dsRows = eventData;
            if (!this.visible)
                return;

            this._showData(eventData);
            this._updateEmptyMessageVisibility(this._dsRows.isFiltered);
        });

        this._dataSource.loadingStarted.add(() => {
            this._dataLoadCounter++;
            this._updateEmptyMessageVisibility();
        });

        this._dataSource.loadingCancelled.add((_sender, eventData) => {
            this._dataLoadCounter--;
            this._updateEmptyMessageVisibility(eventData.isFiltered);
        });

        this._dataSource.loadingEnded.add(async (_sender, eventData) => {
            if (eventData.emptyData === true) {
                this._dataLoadCounter--;
                this._updateEmptyMessageVisibility(eventData.isFiltered);
            }
        });

        this._dataSource.newCommentAdded.add((_sender, eventData) => {
            this._dsRows = eventData;

            const data = eventData.dataGenerator;
            for (const irow of (data() as Iterable<ICommentRowData>)) {
                this.addRow(irow);
                break;
            }
        });
    }

    public get authorColor(): MutableTypedObject<string> {
        return this._authorColor;
    }

    public set authorColor(v: MutableTypedObject<string>) {
        this._authorColor = v;
    }

    /**
     * Příznak, že už byly z DataSourcu načtena veškerá data
     */
    protected _allDataRead: boolean = false;

    public setToQueryMode(_dataLoad: Function): void {
        throw new Error("Method not implemented.");
    }

    public isInQueryMode(): boolean {
        throw new Error("Method not implemented.");
    }

    public headerColor?: string;
    //#endregion Properties

    //#region Constructors
    constructor(self?: any) {
        super(self);
    }

    protected override _constructor(): void {
        super._constructor();

        this.fetchCount = 5;
        this.appendChild(this._dataTable);

        this.emptyMessage = new EmptyTreeDataGridMessage();
        this.appendChild(this.emptyMessage);

        this.classList.add("commentary-frame-grid");

        this.addEventListener("childDataRequest", (e: CustomEvent) => {
            if (e.target instanceof CommentaryFrameDataRow) {
                this._areDataShowedDefferred = PromiseUtils.deferStateful<void>();
                this._loadChildData(e.target).then(() => e.detail.promiseResolvedFnc());
            }
        });

        this._dataRowFactory = () => new CommentaryFrameDataRow();
        this._updateEmptyMessageVisibility();
        this._dataTable.addEventListener("scroll", this._scrollListener = (ev: UIEvent) => {
            this.scrollGrid(ev);
        });
    }
    //#endregion Constructors

    //#region Methods

    /**
     * Metoda pro odstranění všech řádků datagridu
     */
    public override clearDataRows(): void {
        this.dataRows.forEach(row => {
            row.dispose();
            row.remove();
        });
        this.skippedRowsCount = 0;
        this.dataRows = [];
        this._dataTable.innerHTML = "";
    }

    /**
     * Spustí načtení dat do podřízené větve stromu
     * @param _parent Prvek, pro který mají být načteny podřízené záznamy
     */
    protected override async _loadChildData(_parent: CommentaryFrameDataRow): Promise<void> {
        //await this.dataSource.loadChildData(parent ? parent.dataKey : null);
    }

    /**
     * Vytvoří nové řádky s daty
     * @param eventData Informace o načtených datech
     */
    protected override _showData(eventData: ICommentaryDataSourceLoadInfo): void {
        this._bindingData = true;

        if (eventData.startIndex === 0)
            this.clearDataRows();

        this._allDataRead = eventData.lastBatch;
        const data = eventData.dataGenerator;

        this._createRows(data());
        this._renderRows(this._dataTable.childElementCount, this.dataRows.length);
        this._fetchNextBatch(eventData);
        this._bindingData = false;
    }

    private _createRows(data: Iterable<ICommentRowData>): void {
        let i = 0;
        let skippedRows = 0;
        let createdRows = 0;
        const fetchCount = this._currentSkippedRows > 0 ? this._currentSkippedRows : this.fetchCount;
        for (const irow of data) {
            if (!(this.dataSource && irow[this.dataSource.dataKey] != null &&
                this.dataRows.some(dataRow => dataRow.dataKey == irow[this.dataSource.dataKey] ||
                dataRow.childrenRows.some(childDataRow => childDataRow.dataKey == irow[this.dataSource.dataKey])))) { // Pokud již řádek v DG je, nebude se vytvářet znovu
                this._createRow(irow);
                createdRows++;
            } else {
                skippedRows++;
            }

            i++;

            if (this.fetchCount > 0 && createdRows >= fetchCount)
                break;
        }

        this.skippedRowsCount += skippedRows;
        if (this.fetchCount > 0 && i >= fetchCount && createdRows < fetchCount && skippedRows > 0) {
            // pokud jsou k dispozici další data a některé řádky byly přeskočeny a nepřidány do datarows, getujeme počet řádků chybějících do this.fetchCount
            this._currentSkippedRows = fetchCount - createdRows;
            this._getNextData(this._currentSkippedRows);
        } else {
            this._currentSkippedRows = 0;
        }
    }

    private _renderRows(fromRowIndex: number, toRowIndex: number): void {
        for (let i = fromRowIndex; i < toRowIndex; i++) {
            this._renderAllRows(this.dataRows[i], this._dataTable);
        }
    }

    private _renderAllRows(row: CommentaryFrameDataRow, parentContainer: HTMLElement): void {
        parentContainer.appendChild(row.render());
        if (row.childrenRows) {
            for (const childRow of row.childrenRows) {
                //row.childrenContainer.appendChild(childRow.render());
                row.appendRow(childRow.render() as CommentaryFrameDataRow);
                this._renderAllRows(childRow, row.childrenContainer);
            }
        }
    }

    protected async _fetchNextBatch(eventData: IDataSourceLoadInfo): Promise<void> {
        // offsetHeight + 1 kvůli bugu se zaokrouhlováním desetin v Edge 44 a nižší - https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/21405284/
        if (!this._allDataRead && (this._dataTable.offsetHeight + 1 >= this._dataTable.scrollHeight) && this._dataTable.offsetHeight > 0)
            this.dataSource.getNextData(this.startIndex, this.fetchCount, eventData.keepSelectedRecords);
        else if (this._allDataRead)
            this.callEvent("DataShown", { data: eventData.dataGenerator } as IDataGridDataShownEventData);
    }

    public scrollGrid(_ev: UIEvent): void {
        if (this._allDataRead || this._dataTable.scrollTop === 0 || this._currentSkippedRows > 0) // jestli se roluje s vertikalnim scrollem, tak neni treba nacitat data
            return;

        if (this._dataTable && this._dataTable.scrollHeight - this._dataTable.offsetHeight - this._dataTable.scrollTop < 10 && !this._bindingData) {
            this._getNextData(this.fetchCount);
        }
    }

    private _getNextData(count: number): void {
        this._bindingData = true; //je potreba, aby se nastavilo a neslo to sem do doby, nez se vyhodnoti udalost OnLoadData, ktera vraci data
        this.dataSource.getNextData(this.startIndex, count);
    }

    public addRow(data: ICommentRowData): void {
        const newRow = this._createRow(data);
        if (data.parentId != "0" && data.parentId != null)
            this._allRowsMap.get(newRow.parentDataKey)?.appendRow(newRow);
        else
            this._dataTable.insertAdjacentElement("afterbegin", newRow);
        this._updateEmptyMessageVisibility();
    }

    /**
     * Stará se o zobrazení zprávy s informací, že nejsou dostupné žádné záznamy ke zobrazení
     * @param isFiltered Zda je aktivní filtr
     */
    protected _updateEmptyMessageVisibility(isFiltered: boolean = false): void {
        if (this.dataRows.length === 0) {
            this.emptyMessage.visible = true;

            if (this._dataLoadCounter > 0) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.dataLoading;
            } else if (isFiltered) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.filterEmpty;
            } else if (this.emptyMessage.messageType !== EmptyDatagridMessageType.queryModeEmpty) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.initialEmpty;
            }

            return;
        }

        this.emptyMessage.visible = false;
    }

    /**
     * Vytvoří nový řádek s parametry pro jednotlivé položky
     * @param data
     */
    protected override _createRow(data: ICommentRowData): CommentaryFrameDataRow {
        const dataRow = this._dataRowFactory(this, data);
        dataRow.hasNewRights = this.dataSource.rightToNew;
        dataRow.init(data, this);
        dataRow.replyButtonClicked.add((sender, prm) => this.replyButtonClicked.trigger(sender, prm));
        dataRow.avatar.backgroundColor = this._authorColor[data.data.author];
        if (StringUtils.isNullOrEmpty(data.data.hierarchy) || data.data.hierarchy == "0")
            this.dataRows.push(dataRow);
        this._allRowsMap.set(data.data.id.toString(), dataRow);
        data.children.forEach((childRow) => {
            dataRow.childrenRows.push(this._createRow(childRow));
        });
        return dataRow;
    }

    public async reloadData(): Promise<void> {
        this._requestClearRows = true;
        await this.dataSource.refreshData();
    }

    public async loadData(): Promise<void> {
        this._requestClearRows = true;
        await this.dataSource.loadNewData(false);
    }

    // You can also define the other lifecycle methods.
    protected override connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener("resize", this._resizeListener = (_ev: UIEvent) => {
            if (this.clientHeight != 0 && this.clientWidth != 0)
                this.resizeGrid();
        }, { passive: true });
    }

    private _resizeCTS: CancellationTokenSource;

    /**
     * Vola se pokud je resizovan formular nebo cokoliv jineho na co potrebuje grid zareagovat a docist zaznamy kvuli ztrate scrollbaru
     */
    public override resizeGrid(eventData?: IDataSourceLoadInfo): void {
        if (!this._dataTable || !this.dataSource || !this.initialized || this.emptyMessage.messageType === EmptyDatagridMessageType.queryModeEmpty || !this.visible ||
            this._allDataRead || this._dataTable.offsetHeight < this._dataTable.scrollHeight) {
            return;
        }

        //at se to nevola pri kazdem potazeni
        const { promise, cts } = PromiseUtils.cancelPrevAndDelayCall(this._resizeCTS, 300, (token: CancellationToken) => {
            this.dataSource.getNextData(this.startIndex, this.fetchCount, (eventData && eventData.keepSelectedRecords), token);
        });

        promise.catch(PromiseUtils.cancellationCatch);
        this._resizeCTS = cts;
    }

    public override dispose(): void {
        this.clearDataRows();
        this._dataTable.removeEventListener("scroll", this._scrollListener);
        window.removeEventListener("resize", this._scrollListener);
        super.dispose();
    }
    //#endregion Methods
}

/**přidá k datům informace o jeho umístění a stylu */
export interface ICommentaryFrameDataGridAttrInfo {
    avatarInitials: string;
    text: string;
    dateCreate: string;
}

registerCustomElement("tsc-CommentaryFrameGrid", CommentaryFrameGrid);
